import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { platform } from './platform';

/**
 * Save a downloaded file blob to the device. On native it writes to the cache directory and
 * opens the share sheet; on web it triggers a standard anchor download. Shared by any feature
 * that streams a file from the tenant API (documents, facility rule documents, ...).
 */
export async function saveBlobAsDownload(blob: Blob, filename: string, title: string): Promise<void> {
  if (platform.isNative()) {
    const base64 = await blobToBase64(blob);
    const path = `downloads/${filename}`;
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });
    const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
    await Share.share({ title, url: uri, dialogTitle: 'Save file' });
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Save a file from a URL the device can fetch directly (a signed storage link). On native the
 * OS downloads it straight to the cache, with no request timeout and no base64 copy in memory,
 * then the share sheet opens; on web the browser follows the link, whose attachment disposition
 * saves the file.
 */
export async function saveUrlAsDownload(url: string, filename: string, title: string): Promise<void> {
  if (platform.isNative()) {
    const path = `downloads/${filename}`;
    await Filesystem.downloadFile({ url, path, directory: Directory.Cache, recursive: true });
    const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
    await Share.share({ title, url: uri, dialogTitle: 'Save file' });
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Delete the files copied into the device cache for opening or sharing (invoices, documents). */
export async function clearCachedDownloads(): Promise<void> {
  if (!platform.isNative()) return;

  // A folder that was never created rejects; there is nothing to delete then.
  await Promise.all(
    ['documents', 'downloads'].map((path) =>
      Filesystem.rmdir({ path, directory: Directory.Cache, recursive: true }).catch(() => undefined),
    ),
  );
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

/** Make a user-supplied name safe for use as a download filename. */
export function sanitizeFilename(name: string): string {
  const trimmed = name.replace(/[^a-z0-9._-]+/gi, '_').slice(0, 120);
  return trimmed || 'document';
}
